import React, { useState } from "react";
import { sizeNameMap } from "../utils/sizeNameUtils";

const getDealQualityColor = (savingsPercentage) => {
  if (savingsPercentage > 50) return "bg-green-600";
  if (savingsPercentage > 20) return "bg-green-500";
  if (savingsPercentage > 10) return "bg-green-400";
  if (savingsPercentage < -50) return "bg-red-600";
  if (savingsPercentage < -20) return "bg-red-500";
  if (savingsPercentage < -10) return "bg-red-400";
  return "bg-gray-400";
};

const getDealTextColor = (savingsPercentage) => {
  if (savingsPercentage > 50) return "text-green-700";
  if (savingsPercentage > 20) return "text-green-600";
  if (savingsPercentage > 10) return "text-green-500";
  if (savingsPercentage < -50) return "text-red-700";
  if (savingsPercentage < -20) return "text-red-600";
  if (savingsPercentage < -10) return "text-red-500";
  return "text-gray-600";
};

const formatSizeLabel = (size) => {
  if (size === -1 || size === "-1") return "Unknown";
  const numericSize = parseFloat(size);
  if (!isNaN(numericSize)) return `${numericSize}"`;
  return sizeNameMap[size] || size;
};

const BestPriceSummary = ({ bestPricesPerSize, sortSizes, storesData }) => {
  const [hoveredSize, setHoveredSize] = useState(null);
  
  if (!bestPricesPerSize || bestPricesPerSize.length === 0) {
    return null;
  }

  const sortedPrices = [...bestPricesPerSize].sort((a, b) => 
    sortSizes ? sortSizes(a.size, b.size) : 0
  );

  const maxPrice = Math.max(...sortedPrices.map((p) => p.bestPrice));
  const minPrice = Math.min(...sortedPrices.map((p) => p.bestPrice));
  const priceRange = maxPrice - minPrice || 1;

  // Limit the number of visible bars and their max width
  const maxBarWidth = sortedPrices.length < 4 ? 60 : undefined;

  return (
    <div className="best-price-summary bg-white rounded-lg p-3 shadow-sm border border-gray-200">
      <h4 className="text-sm font-medium text-gray-700 mb-2">
        Best Price by Size
      </h4>
      <div className="best-price-summary-chart flex flex-row items-end gap-1 h-16 relative justify-center">
        {sortedPrices.map((priceData) => {
          // Cap bar height between 16px min and 50px max
          const barHeight = 
            Math.min(50, Math.max(16, ((priceData.bestPrice - minPrice) / priceRange) * 40 + 16));
          const dealColor = getDealQualityColor(priceData.savingsPercentage);
          const isHovered = hoveredSize === priceData.size;

          return (
            <div
              key={priceData.size}
              className="flex-1 flex flex-col items-center relative"
              style={{ maxWidth: maxBarWidth }}
              onMouseEnter={() => setHoveredSize(priceData.size)}
              onMouseLeave={() => setHoveredSize(null)}
            >
              <div
                className={`best-price-summary-bar w-full rounded-t transition-all cursor-pointer ${dealColor} ${
                  isHovered ? "opacity-100" : "opacity-80"
                }`}
                style={{ height: `${barHeight}px`, minWidth: "20px", maxWidth: "50px" }}
              />
              <span className="text-xs text-gray-600 mt-1 truncate w-full text-center">
                {formatSizeLabel(priceData.size)}
              </span>
              
              {isHovered && (
                <div className="absolute z-20 bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                  <div className="font-bold">${priceData.bestPrice?.toFixed(2) ?? 'N/A'}</div>
                  {priceData.savingsPercentage !== 0 && (
                    <div className={priceData.savingsPercentage > 0 ? "text-green-300" : "text-red-300"}>
                      {priceData.savingsPercentage > 0 ? "↓" : "↑"} {Math.abs(priceData.savingsPercentage ?? 0).toFixed(0)}% vs avg
                    </div>
                  )}
                  {priceData.historicalPrice != null && (
                    <div className="text-gray-300">
                      Typical: ${Number(priceData.historicalPrice).toFixed(2)}
                    </div>
                  )}
                  <div className="text-gray-400">
                    {storesData?.[priceData.listing?.source] || priceData.listing?.source}
                  </div>
                  <div className="text-gray-400">
                    {priceData.listingCount} listing{priceData.listingCount !== 1 ? "s" : ""}
                  </div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                    <div className="border-4 border-transparent border-t-gray-900" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BestPriceSummary;
export { getDealQualityColor, getDealTextColor };
